'use client';

import { useEffect, useState } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  BadgeCheck,
  BadgeIndianRupee,
  BadgeInfo,
  Ban,
  CalendarIcon,
  RefreshCw,
  ShoppingBag,
  ChevronRight,
  User,
} from 'lucide-react';
import BottomNav from '@/components/layouts/Footer/BottomNav';
import axiosInstance from '@/utils/axiosInstance';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useUser } from '@/contexts/UserContext';
import { sortOrders } from '@/utils/sortOrders';

export default function OrderPage() {
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [statusFilter, setStatusFilter] = useState('Pending');
  const { user, refreshUser } = useUser();
  const [searchParams, setSearchParams] = useSearchParams();

  const fetchOrders = async (status) => {
    try {
      setLoading(true);

      if (!user?.id) {
        setLoading(false);
        return;
      }

      // Make API request
      const response = await axiosInstance.get(
        `${import.meta.env.VITE_API_URL || 'https://api.example.com'}/rest/subziwale/api/v1/orders/vendor?status=${status}&Integer=0`,
        {
          headers: {
            'X-Vendor-Id': user?.id,
            refreshToken: localStorage.getItem('refreshToken'),
            sessionToken: localStorage.getItem('sessionToken'),
          },
        }
      );

      // Don't mix statuses - only show the exact status requested
      const sortedOrders = sortOrders(response?.data, status);
      setOrders(sortedOrders);
      setFilteredOrders(sortedOrders);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab) {
      setStatusFilter(tab);
    }

    window.scrollTo(0, 0);
  }, [user, searchParams]);

  useEffect(() => {
    if (user) {
      fetchOrders(statusFilter);
    } else {
      const contactNo = localStorage.getItem('contactNo');
      refreshUser(contactNo);
    }
  }, [statusFilter, user, refreshUser]);

  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    const params = new URLSearchParams();
    params.set('tab', status);
    setSearchParams(params);
    fetchOrders(status);
  };

  // Status filter options
  const statusOptions = [
    {
      value: 'Pending',
      icon: <BadgeInfo className="w-4 h-4" />,
      color: 'bg-yellow-100 text-yellow-800',
    },
    {
      value: 'Rescheduled',
      icon: <CalendarIcon className="w-4 h-4" />,
      color: 'bg-blue-100 text-blue-800',
    },

    {
      value: 'Completed',
      icon: <BadgeCheck className="w-4 h-4" />,
      color: 'bg-green-100 text-green-800',
    },
    {
      value: 'Paid',
      icon: <BadgeIndianRupee className="w-4 h-4" />,
      color: 'bg-green-100 text-green-800',
    },
    { value: 'Cancelled', icon: <Ban className="w-4 h-4" />, color: 'bg-red-100 text-red-800' },
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="bg-white px-4 py-4 flex items-center border-b sticky top-0 z-10 shadow-sm">
        <div className="flex items-center">
          <div className="w-9 h-9 rounded-full bg-green-100 flex items-center justify-center">
            <ShoppingBag className="w-5 h-5 text-green-600" />
          </div>
        </div>
        <h1 className="text-xl font-semibold flex-1 text-center">Orders Page</h1>
        <Link to="/vendor/profile" className="transition-transform hover:scale-105">
          <Avatar className="border-primary/10">
            <AvatarImage src="https://github.com" alt="@shadcn" />
            <AvatarFallback className="bg-blue-700/10 font-medium">
              {user?.name?.slice(0, 1) === '+' ? (
                <User className="w-8" />
              ) : (
                user?.name?.slice(0, 1) || <User className="w-8" />
              )}
            </AvatarFallback>
          </Avatar>
        </Link>
      </header>

      {/* Main Content */}
      <main className="flex-1 px-4 pt-4 max-w-3xl pb-20 mx-auto w-full">
        {/* Filters */}
        <div className="">
          <div className="flex justify-between items-center mb-3">
            <h2 className="text-xl font-bold text-gray-800">Vendor Orders</h2>
            <button
              onClick={() => fetchOrders(statusFilter)}
              className={`flex items-center gap-2 text-blue-600 px-3 py-1.5 rounded-full bg-blue-50 hover:bg-blue-100 transition-colors ${loading ? 'opacity-70' : ''}`}
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              <span className="text-sm font-medium">Refresh</span>
            </button>
          </div>

          <div className="flex gap-2 overflow-x-auto pb-3 px-1">
            {statusOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => handleStatusFilter(option.value)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-sm whitespace-nowrap text-sm transition-colors ${
                  statusFilter === option.value
                    ? `${option.color} border border-current`
                    : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'
                }`}
              >
                {option.value === 'Completed' ? 'Delivered' : option.value}
              </button>
            ))}
          </div>
        </div>

        {/* Orders List */}
        {loading ? (
          <OrderSkeleton />
        ) : filteredOrders.length === 0 ? (
          <EmptyState statusFilter={statusFilter} onReset={() => handleStatusFilter('Pending')} />
        ) : (
          <div className="space-y-2">
            {filteredOrders.map((order) => (
              <OrderCard key={order.orderId} order={order} />
            ))}
          </div>
        )}
      </main>

      {/* Bottom Navigation */}
      <BottomNav page="orders" />
    </div>
  );
}

const EmptyState = ({ statusFilter, onReset }) => (
  <div className="text-center py-12 px-4 bg-white rounded-lg">
    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
      <ShoppingBag className="w-8 h-8 text-gray-400" />
    </div>
    <h3 className="text-lg font-semibold text-gray-800 mb-2">No orders found</h3>
    <p className="text-gray-600 mb-6">
      {statusFilter !== 'Pending'
        ? `You don't have any ${statusFilter.toLowerCase()} orders yet.`
        : "You haven't received any orders yet."}
    </p>
    {statusFilter !== 'Pending' && (
      <button
        onClick={onReset}
        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
      >
        View pending orders
      </button>
    )}
  </div>
);

// Order Card Component
const OrderCard = ({ order }) => {
  const navigate = useNavigate();

  const getStatusBadge = (status) => {
    switch (status) {
      case 'Completed':
        return (
          <div className="flex flex-col gap-1 items-end">
            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
              <BadgeCheck className="w-3 h-3" />
              Delivered
            </span>
            <span className="text-xs text-red-500 font-medium">Payment Pending</span>
          </div>
        );
      case 'Pending':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <BadgeInfo className="w-3 h-3" />
            {status}
          </span>
        );
      case 'Rescheduled':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            <CalendarIcon className="w-3 h-3" />
            {status}
          </span>
        );
      case 'Cancelled':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <Ban className="w-3 h-3" />
            {status}
          </span>
        );
      case 'Paid':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            <BadgeIndianRupee className="w-3 h-3" />
            {status}
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            <CalendarIcon className="w-3 h-3" />
            {status}
          </span>
        );
    }
  };

  const getDeliveryBadge = (order) => {
    if (order.expressDelivery) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-200">
          Express
        </span>
      );
    } else if (order.selfPickup) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-50 text-purple-700 border border-purple-200">
          Self Pickup
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
          Standard
        </span>
      );
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatTimeSlot = (timeSlot) => {
    if (!timeSlot) return 'Time slot not available';

    const [start, end] = timeSlot.split('-');
    const formatTime = (time) => {
      if (!time) return 'N/A';
      const [hours, minutes] = time.split(':');
      const date = new Date(2000, 0, 1, hours, minutes);
      return date.toLocaleString('en-US', {
        hour: 'numeric',
        minute: 'numeric',
        hour12: true,
      });
    };
    return `${formatTime(start)} to ${formatTime(end)}`;
  };

  return (
    <div
      onClick={() => navigate(`/vendor/orders/${order?.orderId}`)}
      className="bg-white rounded-lg overflow-hidden border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-200"
    >
      {/* Order Header */}
      <div className="bg-white px-4 py-2 border-b border-gray-200">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="font-medium text-gray-900">{order.customerName}</h3>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <span className="flex items-center gap-1">
                <CalendarIcon className="w-3.5 h-3.5" />
                {formatDate(order.orderDate)}
              </span>
              <span className="text-gray-400">•</span>
              <span>{formatTime(order.orderDate)}</span>
            </div>
          </div>
          {getStatusBadge(order.status)}
        </div>
      </div>

      {/* Order Content */}
      <div className="px-4 py-2">
        {/* Products */}
        <div className="flex flex-wrap gap-2 mb-1">
          {order.products.slice(0, 4).map((product, idx) => (
            <div
              key={idx}
              className="relative w-10 h-10 rounded-md overflow-hidden bg-gray-100 border border-gray-200"
            >
              {product.productImageUrl ? (
                <img
                  loading="lazy"
                  src={product.productImageUrl || '/placeholder.svg'}
                  alt={product.productName}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-xs bg-gray-200">
                  No image
                </div>
              )}
            </div>
          ))}
          {order.products.length > 4 && (
            <div className="w-4 h-10 rounded-md flex items-center justify-center text-sm font-medium text-gray-700">
              +{order.products.length - 4}
            </div>
          )}
        </div>

        {/* Delivery Info */}
        <div className="text-sm text-gray-600 mt-1">
          Delivery: {formatDate(order?.deliveryDate)}{' '}
          {order?.timeSlot ? formatTimeSlot(order?.timeSlot) : 'Time slot not available'}
        </div>

        {/* Order Footer */}
        <div className="flex justify-between items-center pt-2 border-t border-gray-100 mt-2">
          <div className="flex items-center gap-2">
            {getDeliveryBadge(order)}
            <button
              onClick={() => navigate(`/vendor/orders/${order?.orderId}`)}
              className="flex items-center text-blue-600 hover:text-blue-800 transition-colors"
            >
              <span className="text-sm cursor-pointer font-medium">Details</span>
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          <div className="text-lg font-semibold text-gray-900">₹{order.total.toFixed(2)}</div>
        </div>
      </div>
    </div>
  );
};

// Order Skeleton Component
const OrderSkeleton = () => {
  return (
    <div className="space-y-4">
      {[1, 2, 3].map((i) => (
        <div
          key={i}
          className="bg-white rounded-lg overflow-hidden border border-gray-200 animate-pulse"
        >
          <div className="px-4 py-3 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <div>
                <div className="h-5 bg-gray-200 rounded w-32 mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-40"></div>
              </div>
              <div className="h-6 bg-gray-200 rounded-full w-20"></div>
            </div>
          </div>
          <div className="p-4">
            <div className="flex gap-2 mb-4">
              <div className="w-10 h-10 bg-gray-200 rounded-md"></div>
              <div className="w-10 h-10 bg-gray-200 rounded-md"></div>
              <div className="w-10 h-10 bg-gray-200 rounded-md"></div>
              <div className="w-10 h-10 bg-gray-200 rounded-md"></div>
            </div>
            <div className="h-4 bg-gray-200 rounded w-full mb-4"></div>
            <div className="flex justify-between items-center pt-2">
              <div className="h-6 bg-gray-200 rounded w-20"></div>
              <div className="h-6 bg-gray-200 rounded-full w-24"></div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};
